import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";

// @ts-ignore - JS コンポーネント（型定義なし）
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
// @ts-ignore - JSX コンポーネント（型定義なし）
import Tab from "../../components/tab/Tab";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../components/block/TitleBlock";
import HelpHint from "../../components/help-hint/HelpHint";
import SvgLoadingAnimation from "../../components/loading";
import InlineLoading from "../../components/loading/InlineLoading";
import Table, { TableColumn } from "../../components/table/Table";
import Input from "../../components/form-item/Input";
import Button from "../../components/button/Button";
import IconButton from "../../components/button/IconButton";
import ConfirmModal from "../../components/modal/ConfirmModal";
import ToolTip from "../../components/tooltip/ToolTip";
import UserAvatar from "../../components/avatar/UserAvatar";
import FilterChip from "../../components/filter-chip/FilterChip";
import StatusBadge from "../../components/status-badge/StatusBadge";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../../components/date-display/DateDisplay";
import {
  useGetAccountsQuery,
  useUpdateAccountUserMutation,
  useDeleteAccountUserMutation,
  type AccountRole,
  type AccountSortField,
  type AccountStatus,
  type AccountUser,
  type AccountUserPatch,
} from "../../app/services/accounts";
import { clearActAs, getActAs, startActAs } from "../../utils/actAs";
import AccountWithdrawalModal from "./components/AccountWithdrawalModal";
import AccountTransactionsModal from "./components/AccountTransactionsModal";
import EditableTextCell from "./components/EditableTextCell";

// ===== 定数 =====

const PAGE_LIMIT = 30;
const SEARCH_DEBOUNCE_MS = 300;

/** ADMIN ビューのタブ（ラベル → 一覧 API の role 値） */
const ADMIN_TABS: { label: string; role: AccountRole }[] = [
  { label: "クライアント", role: "CLIENT" },
  { label: "管理者", role: "ADMIN" },
  { label: "マネージャー", role: "MANAGER" },
];

type SortState = { field: AccountSortField; order: "asc" | "desc" } | null;

// ===== styled-components =====

// タスクボードと同じ幅制限なしのフルワイドレイアウト（`u-wrapper-full` 相当）。
const PageWrapper = styled.div.attrs({
  className: "u-col u-w100 u-gap16",
})`
  padding: 24px 16px 80px;
`;

const SearchBar = styled.div`
  width: 100%;
  max-width: 400px;
`;

const EmptyCell = styled.td.attrs({
  className: "bodytext-13 u-text-light-gray u-text-center",
})`
  padding: 32px 8px !important;
`;

/** 無効化されたアクション（グレーアウト・遷移不可）のラベル */
const DisabledAction = styled.span.attrs({
  className: "heading-13 u-text-light-gray",
})`
  cursor: not-allowed;
  white-space: nowrap;
`;

const Sentinel = styled.div`
  width: 100%;
  height: 1px;
`;

// ===== hooks =====

const useDebouncedValue = (value: string, delay: number): string => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
};

/**
 * 検索 + 無限スクロール対応のアカウント一覧フック。
 * ロール切替・検索語変更・ソート変更でローカル累積をリセットする
 * （FollowersAndFollowing の useUserListPagination と同パターン）。
 */
const useAccountsPagination = ({
  role,
  q,
  sort,
  status,
}: {
  role: AccountRole;
  q: string;
  sort: SortState;
  status?: AccountStatus;
}) => {
  const [offset, setOffset] = useState(0);
  const [accumulated, setAccumulated] = useState<AccountUser[]>([]);

  const queryArg = useMemo(
    () => ({
      role,
      q: q || undefined,
      limit: PAGE_LIMIT,
      offset,
      sort: sort?.field,
      order: sort?.order,
      status,
    }),
    [role, q, offset, sort, status],
  );
  const result = useGetAccountsQuery(queryArg);

  useEffect(() => {
    setOffset(0);
    setAccumulated([]);
  }, [role, q, sort, status]);

  // ページ取得結果を累積（offset=0 のときは置き換え、それ以外は追記）。
  useEffect(() => {
    const data = result.data?.data;
    if (!data) return;
    setAccumulated((prev) => {
      if (data.offset === 0) return data.users;
      const seen = new Set(prev.map((u) => u.id));
      return [...prev, ...data.users.filter((u) => !seen.has(u.id))];
    });
  }, [result.data]);

  const hasNext = !!result.data?.data?.has_next;
  const isFetching = result.isFetching;

  const loadMore = useCallback(() => {
    if (!hasNext || isFetching) return;
    setOffset((prev) => prev + PAGE_LIMIT);
  }, [hasNext, isFetching]);

  // インライン編集の保存成功後にロード済み行をローカル更新する
  // （一覧を再フェッチすると無限スクロールの累積がリセットされるため）。
  const patchUser = useCallback(
    (userId: number, patch: Partial<AccountUser>) => {
      setAccumulated((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, ...patch } : u)),
      );
    },
    [],
  );

  // 削除成功後にロード済み行をローカル除去する（patchUser と同じ理由）。
  const removeUser = useCallback((userId: number) => {
    setAccumulated((prev) => prev.filter((u) => u.id !== userId));
  }, []);

  return {
    users: accumulated,
    isLoading: result.isLoading,
    isFetching,
    isError: result.isError,
    hasNext,
    loadMore,
    patchUser,
    removeUser,
  };
};

// ===== helpers =====

const getAccountDisplayName = (user: AccountUser): string =>
  user.display_name_jp || user.display_name_en || `ユーザーID ${user.id}`;

/** 招待中（未登録）のアカウントか */
const isInvitedUser = (user: AccountUser): boolean =>
  user.account_status === "invited";

/**
 * 確認モーダル・トーストで使うアカウントの呼び名。
 * 招待中ユーザーは表示名が空なのでメールアドレスで表す。
 */
const getAccountLabel = (user: AccountUser): string =>
  isInvitedUser(user)
    ? user.email || `ユーザーID ${user.id}`
    : getAccountDisplayName(user);

/** 空値は em ダッシュで表示する */
const cellText = (value: string | null | undefined): string => value || "—";

// ===== メインコンポーネント =====

/**
 * アカウント管理ページ（/account）。
 *
 * - MANAGER: クリエイター一覧。振込申請 / 進行中（モーダル）、
 *   マイページ / プロフィール（act-as 代理操作）付き。
 * - ADMIN: クライアント / 管理者 / マネージャーのロール別タブ一覧（抜粋版）。
 *   マイページ（act-as 代理操作）付き。
 * - 全列ヘッダークリックでサーバーサイドソート（昇順 → 降順 → 既定順）。
 * - 最右列の削除アイコン → 確認モーダル → soft delete。
 */
const Account: React.FC = () => {
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery();

  // act-as が残ったまま戻ってきた場合は、状態を破棄してリロードし
  // 実行者本人のセッションに戻す（全 RTK Query キャッシュをリセット）。
  const staleActAs = !!getActAs();
  useEffect(() => {
    if (staleActAs) {
      clearActAs();
      window.location.reload();
    }
  }, [staleActAs]);

  const role: string | undefined = currentUser?.data?.role;
  const isAdmin = role === "ADMIN";
  const isManager = role === "MANAGER";

  // ADMIN のタブ（MANAGER はクリエイター固定）
  const [activeTabLabel, setActiveTabLabel] = useState(ADMIN_TABS[0].label);
  const listRole: AccountRole = isManager
    ? "CREATOR"
    : (ADMIN_TABS.find((t) => t.label === activeTabLabel)?.role ?? "CLIENT");

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  // 「招待中のみ」フィルタ（ロールタブと直交）。オファー / プロジェクト招待で
  // 先行作成されたまま本登録が済んでいないアカウントに絞る。
  const [showInvitedOnly, setShowInvitedOnly] = useState(false);

  // 列ヘッダーのサーバーサイドソート（昇順 → 降順 → 既定順の 3 状態トグル）
  const [sortState, setSortState] = useState<SortState>(null);
  useEffect(() => {
    setSortState(null);
  }, [listRole]);

  const handleSort = useCallback((field: AccountSortField) => {
    setSortState((prev) => {
      if (prev?.field !== field) return { field, order: "asc" };
      if (prev.order === "asc") return { field, order: "desc" };
      return null; // 3 回目のクリックでサーバー既定順（最終更新日降順）へ戻す
    });
  }, []);

  const {
    users: rows,
    isLoading,
    isFetching,
    isError,
    hasNext,
    loadMore,
    patchUser,
    removeUser,
  } = useAccountsPagination({
    role: listRole,
    q: debouncedSearch,
    sort: sortState,
    status: showInvitedOnly ? "invited" : undefined,
  });

  // インライン編集（表示名・タイトル・所属）: 対象ユーザー本人としての
  // PATCH /api/me（act-as）。成功したらロード済み行をローカル更新する。
  const [updateAccountUser] = useUpdateAccountUserMutation();
  const handleCellSave = useCallback(
    async (
      userId: number,
      field: keyof AccountUserPatch,
      value: string,
    ): Promise<void> => {
      try {
        await updateAccountUser({ userId, patch: { [field]: value } }).unwrap();
        patchUser(userId, { [field]: value });
      } catch {
        toast.error("保存に失敗しました。時間をおいて再度お試しください。");
        throw new Error("account inline edit failed");
      }
    },
    [updateAccountUser, patchUser],
  );

  // モーダル対象（MANAGER のみ使用）
  const [withdrawalTarget, setWithdrawalTarget] = useState<AccountUser | null>(
    null,
  );
  const [transactionsTarget, setTransactionsTarget] =
    useState<AccountUser | null>(null);

  // 削除確認モーダル（招待中アカウントは「招待の取り消し」として動作）
  const [deleteTarget, setDeleteTarget] = useState<AccountUser | null>(null);
  const [deleteAccountUser, { isLoading: isDeleting }] =
    useDeleteAccountUserMutation();
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const invited = isInvitedUser(deleteTarget);
    try {
      await deleteAccountUser(deleteTarget.id).unwrap();
      removeUser(deleteTarget.id);
      toast.success(
        invited
          ? `「${getAccountLabel(deleteTarget)}」への招待を取り消しました`
          : `「${getAccountLabel(deleteTarget)}」のアカウントを削除しました`,
      );
      setDeleteTarget(null);
    } catch (err) {
      // 招待取り消し不可（進行中オファーあり等）は BE の message をそのまま出す
      const message = (err as { data?: { message?: string } })?.data?.message;
      toast.error(
        message ||
          (invited
            ? "招待の取り消しに失敗しました"
            : "アカウントの削除に失敗しました"),
      );
    }
  }, [deleteTarget, deleteAccountUser, removeUser]);

  // 無限スクロール
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleOpenMyPage = useCallback((user: AccountUser) => {
    startActAs(
      { userId: user.id, displayName: getAccountDisplayName(user) },
      "/my-page",
    );
  }, []);

  const handleOpenProfile = useCallback((user: AccountUser) => {
    startActAs(
      { userId: user.id, displayName: getAccountDisplayName(user) },
      "/my-profile",
    );
  }, []);

  if (isUserLoading || staleActAs) {
    return <SvgLoadingAnimation />;
  }

  if (!isAdmin && !isManager) {
    return <Navigate to="/projects" replace />;
  }

  // ソート可能ヘッダー（ShotTable と同じ見た目）
  const sortHeader = (label: string, field: AccountSortField) => (
    <>
      <span>{label}</span>
      {sortState?.field === field && (
        <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
          {sortState.order === "asc" ? "arrow_upward" : "arrow_downward"}
        </span>
      )}
    </>
  );

  /** ソート可能列の共通定義（ヘッダークリックでサーバーソート） */
  const sortableColumn = (
    field: AccountSortField,
    label: string,
    extra?: Partial<TableColumn>,
  ): TableColumn => ({
    key: field,
    header: sortHeader(label, field),
    onHeaderClick: () => handleSort(field),
    headerTitle: "クリックでソート",
    ...extra,
  });

  const columns: TableColumn[] = [
    { key: "avatar", header: "", width: 48, minWidth: 48, resizable: false },
    sortableColumn("display_name_jp", "表示名", { minWidth: 120 }),
    sortableColumn("display_name_en", "表示名（英）", { minWidth: 110 }),
    sortableColumn("title_jp", "タイトル", { minWidth: 100 }),
    sortableColumn("title_en", "タイトル（英）", { minWidth: 100 }),
    sortableColumn("affiliation_jp", "所属", { minWidth: 100 }),
    sortableColumn("affiliation_en", "所属（英）", { minWidth: 100 }),
    sortableColumn("email", "メールアドレス", { minWidth: 170 }),
    sortableColumn("updated_at", "最終更新日", { width: 120, minWidth: 100 }),
    ...(isManager
      ? [
          {
            key: "withdrawal",
            header: "振込申請",
            width: 96,
            minWidth: 80,
            resizable: false,
          },
          {
            key: "transactions",
            header: "進行中",
            width: 96,
            minWidth: 80,
            resizable: false,
          },
        ]
      : []),
    {
      key: "my_page",
      header: "マイページ",
      width: 96,
      minWidth: 80,
      resizable: false,
    },
    ...(isManager
      ? [
          {
            key: "profile",
            header: "プロフィール",
            width: 108,
            minWidth: 88,
            resizable: false,
          },
        ]
      : []),
    { key: "delete", header: "", width: 48, minWidth: 48, resizable: false },
  ];

  const renderRows = () => {
    if (isLoading) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>
            <InlineLoading visible />
          </EmptyCell>
        </tr>
      );
    }
    if (isError) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>
            アカウント一覧の取得に失敗しました。
          </EmptyCell>
        </tr>
      );
    }
    if (rows.length === 0) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>
            {debouncedSearch
              ? "該当するアカウントが見つかりません"
              : "アカウントがありません"}
          </EmptyCell>
        </tr>
      );
    }
    return rows.map((user) => {
      const invited = isInvitedUser(user);
      // 編集可能セル（表示名・タイトル・所属）の共通レンダラー。
      // 招待中アカウントは本人未登録（act-as 不可）のため編集不可で表示する。
      const editableCell = (field: keyof AccountUserPatch, label: string) =>
        invited ? (
          <td className="bodytext-13 u-text-light-gray">
            {cellText(user[field])}
          </td>
        ) : (
          <td className="bodytext-13">
            <EditableTextCell
              value={user[field]}
              ariaLabel={`${getAccountDisplayName(user)} の${label}`}
              onSave={(value) => handleCellSave(user.id, field, value)}
            />
          </td>
        );
      // プロフィールは公開中（slug 設定済み + 公開状態）のみ遷移可（⑥）
      const isProfilePublic = user.vanity_slug != null;
      return (
        <tr key={user.id}>
          {/* table-cell-visible: 有機的シェイプのアバターの影を
              セルの overflow: hidden（省略記号）から解放する */}
          <td className="table-cell-visible">
            <div className="u-row-center">
              <UserAvatar
                userId={user.id}
                avatar={user.avatar_url}
                displayName={getAccountDisplayName(user)}
                size={32}
              />
            </div>
          </td>
          {invited ? (
            // 招待中: 表示名は未設定なので状態バッジを出す
            // （プロジェクトバナーのメンバーリストと同じ見せ方）
            <td className="table-cell-visible">
              <StatusBadge label="招待中" color="border" />
            </td>
          ) : (
            editableCell("display_name_jp", "表示名")
          )}
          {editableCell("display_name_en", "表示名（英）")}
          {editableCell("title_jp", "タイトル")}
          {editableCell("title_en", "タイトル（英）")}
          {editableCell("affiliation_jp", "所属")}
          {editableCell("affiliation_en", "所属（英）")}
          <ToolTip description={user.email ?? ""} truncatedOnly>
            <td className="bodytext-13">{cellText(user.email)}</td>
          </ToolTip>
          <td className="bodytext-13 u-text-light-gray">
            {user.updated_at ? (
              <DateDisplay
                date={user.updated_at}
                type={DATE_DISPLAY_TYPE.DATE_TIME}
              />
            ) : (
              "—"
            )}
          </td>
          {isManager && (
            <>
              <td>
                {user.has_withdrawable ? (
                  <Button
                    size="small"
                    variant="text"
                    width="fit"
                    onClick={() => setWithdrawalTarget(user)}
                  >
                    振込申請
                  </Button>
                ) : (
                  <ToolTip description="振込申請できる残高がありません">
                    <DisabledAction>振込申請</DisabledAction>
                  </ToolTip>
                )}
              </td>
              <td>
                {user.has_in_progress ? (
                  <Button
                    size="small"
                    variant="text"
                    width="fit"
                    onClick={() => setTransactionsTarget(user)}
                  >
                    進行中
                  </Button>
                ) : (
                  <ToolTip description="進行中の取引がありません">
                    <DisabledAction>進行中</DisabledAction>
                  </ToolTip>
                )}
              </td>
            </>
          )}
          <td>
            {invited ? (
              <ToolTip description="本登録が完了していないため開けません">
                <DisabledAction>マイページ</DisabledAction>
              </ToolTip>
            ) : (
              <Button
                size="small"
                variant="text"
                width="fit"
                onClick={() => handleOpenMyPage(user)}
              >
                マイページ
              </Button>
            )}
          </td>
          {isManager && (
            <td>
              {isProfilePublic ? (
                <Button
                  size="small"
                  variant="text"
                  width="fit"
                  onClick={() => handleOpenProfile(user)}
                >
                  プロフィール
                </Button>
              ) : (
                <ToolTip description="プロフィールが非公開のため開けません">
                  <DisabledAction>プロフィール</DisabledAction>
                </ToolTip>
              )}
            </td>
          )}
          <td style={{ textAlign: "center" }}>
            {/* 自分自身の行は削除不可（BE でも 400 ガード） */}
            {user.id !== currentUser?.data?.id && (
              <IconButton
                icon="delete"
                size={18}
                title={invited ? "招待を取り消す" : "アカウントを削除"}
                ariaLabel={
                  invited
                    ? `${getAccountLabel(user)} への招待を取り消す`
                    : `${getAccountDisplayName(user)} を削除`
                }
                onClick={() => setDeleteTarget(user)}
              />
            )}
          </td>
        </tr>
      );
    });
  };

  return (
    <PageWrapper>
      {/* 説明は ? アイコンのホバーに畳む（本題の表を上に詰める） */}
      <div className="u-row-between u-items-start u-w100 u-gap8">
        <TitleBlock title="ACCOUNT" />
        <HelpHint description={
          isManager
            ? "クリエイターのアカウントを管理します。マイページ・プロフィールは本人と同様に代理操作できます。"
            : "クライアント・管理者・マネージャーのアカウントを管理します。マイページは本人と同様に代理操作できます。"
        } />
      </div>

      {isAdmin && (
        <Tab
          list={ADMIN_TABS.map((t) => t.label)}
          activeTab={activeTabLabel}
          onTabChange={(label: string) => setActiveTabLabel(label)}
        />
      )}

      <div className="u-row u-items-center u-gap16 u-w100">
        <SearchBar>
          <Input
            type="search"
            placeholder="表示名・氏名・メールアドレスで検索"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </SearchBar>
        {/* 招待中（未登録）アカウントのみに絞るフィルタ。ロールタブと直交 */}
        <FilterChip
          selected={showInvitedOnly}
          onClick={() => setShowInvitedOnly((prev) => !prev)}
        >
          招待中のみ
        </FilterChip>
      </div>

      <Table columns={columns} minWidth={isManager ? 1408 : 1108}>
        {renderRows()}
      </Table>

      {/* 無限スクロールの監視要素 + 追加ロード表示 */}
      <Sentinel ref={sentinelRef} />
      {isFetching && !isLoading && <InlineLoading visible />}
      {!hasNext && !isLoading && rows.length > 0 && (
        <p className="bodytext-11 u-text-light-gray u-text-center u-w100">
          {rows.length} 件を表示中
        </p>
      )}

      {/* 振込申請モーダル（MANAGER） */}
      {withdrawalTarget && (
        <AccountWithdrawalModal
          user={withdrawalTarget}
          displayName={getAccountDisplayName(withdrawalTarget)}
          onClose={() => setWithdrawalTarget(null)}
        />
      )}

      {/* 進行中モーダル（MANAGER） */}
      {transactionsTarget && (
        <AccountTransactionsModal
          user={transactionsTarget}
          onClose={() => setTransactionsTarget(null)}
        />
      )}

      {/* ユーザー削除確認（招待中は「招待の取り消し」文言に切り替え） */}
      {deleteTarget && (
        <ConfirmModal
          message={
            isInvitedUser(deleteTarget)
              ? `「${getAccountLabel(deleteTarget)}」への招待を取り消しますか？\n` +
                "招待リンクは無効になり、このメールアドレスは再び利用できるようになります。"
              : `「${getAccountLabel(deleteTarget)}」のアカウントを削除しますか？\n` +
                "サインインできなくなります。この操作は取り消せません。"
          }
          confirmLabel={isInvitedUser(deleteTarget) ? "招待を取り消す" : "削除"}
          loading={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </PageWrapper>
  );
};

export default Account;
